import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import axiosClient from './request-client';
import {
  League,
  LeaguePayment,
  LeaguePositionPoints,
  LeagueRanking,
  Nullable,
  TournamentLeague,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import { z } from 'zod';

export enum LeagueType {
  Open = 'OPEN',
  Closed = 'CLOSED',
}

export enum LeagueRule {
  Bounty4Points = 'Bounty4Points',
  Bounties4Points = 'Bounties4Points',
  Bounty4Boosters = 'Bounty4Boosters',
  NotSoFastChamp = 'NotSoFastChamp',
  AttendancePoints = 'AttendancePoints',
  UndefeatedBonus = 'UndefeatedBonus',
  BestResultPerRound = 'BestResultPerRound',
  PositionMultiplier = 'PositionMultiplier',
  PositionBasedPoints = 'PositionBasedPoints',
  StageAttendanceBonus = 'StageAttendanceBonus',
  PerfectStageAttendance = 'PerfectStageAttendance',
  StageTiebreaker = 'StageTiebreaker',
  DuelistHunter = 'DuelistHunter',
  DuelistKingdom = 'DuelistKingdom',
  WinsBasedPoints = 'WinsBasedPoints',
  ExtraPoints = 'ExtraPoints',
  MatchCountTiebreaker = 'MatchCountTiebreaker',
  LoserSubsidy = 'LoserSubsidy',
  NoCampingBonus = 'NoCampingBonus',
}

export enum LeagueStatus {
  Upcoming = 'UPCOMING',
  InProgress = 'IN_PROGRESS',
  Finished = 'FINISHED',
  Cancelled = 'CANCELLED',
}

export const createLeagueSchema = z
  .object({
    name: z.string(),
    slug: z.string().optional(),
    organizerId: z.number().min(1),
    dateFrom: z.date(),
    dateTo: z.date(),

    numberOfDates: z.number().min(1).optional(),
    type: z.nativeEnum(LeagueType),
    status: z.nativeEnum(LeagueStatus).nullable().optional(),

    description: z.string().optional(),
    formatId: z.number().min(0).optional(),
    // Todos los formatos de la liga; `formatId` es el principal y va entre ellos.
    formatIds: z.array(z.number().min(1)).min(1).optional(),
    gameTypeId: z.number().min(0).optional(),
    ogImage: z.string().nullable().optional(),

    hasExtraPayment: z.boolean().optional(),
    extraPaymentAmount: z.number().min(0).optional(),
  })
  .superRefine((data, ctx) => {
    // Validate dateFrom < dateTo
    if (data.dateFrom && data.dateTo && data.dateFrom >= data.dateTo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dateTo'],
        message: 'La fecha de fin debe ser posterior a la fecha de inicio',
      });
    }
  });

export const listLeagues = async (
  user: Nullable<UserWithToken>,
  filters: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<League>>(
    gql`
      query leagues($filters: LeagueFilters) {
        data: leagues(filters: $filters) {
          data {
            id
            name
            slug
            type
            status
            formatId
            formatIds
            ogImage
            organizer {
              id
              name
              logoUrl
            }
            dateFrom
            dateTo
          }
          meta {
            lastPage
            total
          }
        }
      }
    `,
    { filters }
  );

// Get leagues where the user is a player
export const getMyLeagues = async (user: UserWithToken) =>
  getGraphQLQueryClient(user).request<WithMeta<League>>(
    gql`
      query myLeagues($filters: LeagueFilters) {
        data: leagues(filters: $filters) {
          data {
            id
            name
            slug
            type
            status
            formatId
            formatIds
            numberOfDates
            dateFrom
            dateTo
            ogImage
            organizer {
              id
              name
              logoUrl
            }
            players {
              id
              userId
              banned
              user {
                id
                name
                lastName
              }
            }
            tournaments {
              id
              roundNumber
              tournament {
                id
                title
                status
                dateFrom
              }
            }
          }
          meta {
            lastPage
            total
          }
        }
      }
    `,
    { filters: { playerId: user.id, limit: 100 } }
  );

const leagueDetailFields = `
  id
  name
  slug
  dateFrom
  dateTo
  numberOfDates
  type
  status
  formatId
  formatIds
  ogImage
  hasExtraPayment
  extraPaymentAmount

  organizer {
    id
    name
  }

  players {
    id
    banned
    user {
      id
      name
      lastName
      cossyId
      masterDuelId
      duelLinksId
    }
  }

  tournaments {
    id
    roundNumber
    pointsMultiplier
    tournament {
      id
      title
      dateFrom
      dateTo
      status
      organizer {
        id
        name
      }
      effectiveBanlist {
        id
        name
        formatId
        logoUrl
        shareHash
        organizerSlug
      }
    }
  }

  rules {
    id
    rule
    description
    configs {
      id
      leagueRuleId
      rounds
      multiplier
      excludeTopN
    }
    positionPoints {
      minPlayers
      position
      points
    }
  }
`;

export const getLeague = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<League>>(
    gql`
      query league($id: Int!) {
        data: league(id: $id) {
          ${leagueDetailFields}
        }
      }
    `,
    { id }
  );

export const getLeagueBySlug = async (
  user: Nullable<UserWithToken>,
  slug: string
) =>
  getGraphQLQueryClient(user).request<WithData<League>>(
    gql`
      query leagueBySlug($slug: String!) {
        data: leagueBySlug(slug: $slug) {
          ${leagueDetailFields}
        }
      }
    `,
    { slug }
  );

// Lightweight lookup used to validate slug availability while typing in the
// league form. Only selects `id`, so it's cheap to call on every keystroke
// (unlike getLeagueBySlug, which pulls the full league detail payload).
export const getLeagueIdBySlug = async (
  user: Nullable<UserWithToken>,
  slug: string
) =>
  getGraphQLQueryClient(user).request<WithData<{ id: number } | null>>(
    gql`
      query leagueBySlug($slug: String!) {
        data: leagueBySlug(slug: $slug) {
          id
        }
      }
    `,
    { slug }
  );

// Resolves a league by either its numeric id or its slug, so legacy
// /ligas/{id} links keep working alongside the new /ligas/{slug} routes.
export const getLeagueByIdentifier = async (
  user: Nullable<UserWithToken>,
  idOrSlug: string | number
) => {
  const value = String(idOrSlug);

  return /^\d+$/.test(value)
    ? getLeague(user, Number(value))
    : getLeagueBySlug(user, value);
};

export const createLeague = (
  user: Nullable<UserWithToken>,
  input: z.infer<typeof createLeagueSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<League>>(
    gql`
      mutation createLeague($input: CreateLeagueInput!) {
        data: createLeague(input: $input) {
          id
        }
      }
    `,
    { input }
  );

export const updateLeague = (
  user: Nullable<UserWithToken>,
  leagueId: number,
  input: z.infer<typeof createLeagueSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<League>>(
    gql`
      mutation updateLeague($id: Int!, $input: CreateLeagueInput!) {
        data: updateLeague(id: $id, input: $input) {
          id
        }
      }
    `,
    { id: leagueId, input }
  );

export const getLeagueRankings = async (
  user: Nullable<UserWithToken>,
  leagueId: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueRanking[]>>(
    gql`
      query leagueRankings($leagueId: Int!) {
        data: leagueRankings(leagueId: $leagueId) {
          leagueId
          userId
          position
          points
          wins
          losses
          draws
          winAverage
          tournamentsPlayed
          tops
          tournamentWins
          user {
            id
            name
            lastName
            cossyId
          }
        }
      }
    `,
    { leagueId }
  );

export type LeaguePointsBreakdown = {
  tournamentName: string;
  deckName?: string | null;
  position: number;
  participants: number;
  basePoints: number;
  multiplier: number;
  attendanceBonus: number;
  undefeatedBonus: number;
  bountyBonus: number;
  duelistHunterBonus: number;
  subsidyBonus: number;
  noCampingBonus: number;
  stageAttendanceBonus: number;
  perfectStageBonus: number;
  starBonus: number;
  extraPoints: number;
  extraPointsReason: string | null;
  roundPoints: number;
};

export type LeagueRankingByRoundEntry = LeagueRanking & {
  pointsBreakdown?: LeaguePointsBreakdown[] | null;
};

export type LeagueRankingByRound = {
  roundNumber: number;
  rankings: LeagueRankingByRoundEntry[];
};

export const getLeagueRankingByRound = async (
  user: Nullable<UserWithToken>,
  leagueId: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueRankingByRound[]>>(
    gql`
      query leagueRankingByRound($leagueId: Int!) {
        data: leagueRankingByRound(leagueId: $leagueId) {
          roundNumber
          rankings {
            leagueId
            userId
            position
            points
            wins
            losses
            draws
            winAverage
            tournamentsPlayed
            tops
            tournamentWins
            user {
              id
              name
              lastName
              cossyId
            }
            pointsBreakdown {
              tournamentName
              deckName
              position
              participants
              basePoints
              multiplier
              attendanceBonus
              undefeatedBonus
              bountyBonus
              duelistHunterBonus
              subsidyBonus
              noCampingBonus
              stageAttendanceBonus
              perfectStageBonus
              starBonus
              extraPoints
              extraPointsReason
              roundPoints
            }
          }
        }
      }
    `,
    { leagueId }
  );

export type LeagueTournamentParticipation = {
  tournamentId: number;
  userIds: number[];
};

export const getLeagueParticipation = async (
  user: Nullable<UserWithToken>,
  leagueId: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueTournamentParticipation[]>>(
    gql`
      query leagueParticipation($leagueId: Int!) {
        data: leagueParticipation(leagueId: $leagueId) {
          tournamentId
          userIds
        }
      }
    `,
    { leagueId }
  );

export const addLeagueRule = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  rule: LeagueRule,
  description?: string | null
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; rule: string; description: string | null }>
  >(
    gql`
      mutation addLeagueRule(
        $leagueId: Int!
        $rule: LeagueRule!
        $description: String
      ) {
        data: addLeagueRule(
          leagueId: $leagueId
          rule: $rule
          description: $description
        ) {
          id
          rule
          description
        }
      }
    `,
    { leagueId, rule, description }
  );

export const updateLeagueRule = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  rule: LeagueRule,
  description?: string | null
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; rule: string; description: string | null }>
  >(
    gql`
      mutation updateLeagueRule(
        $leagueId: Int!
        $rule: LeagueRule!
        $description: String
      ) {
        data: updateLeagueRule(
          leagueId: $leagueId
          rule: $rule
          description: $description
        ) {
          id
          rule
          description
        }
      }
    `,
    { leagueId, rule, description }
  );

export const removeLeagueRule = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  rule: LeagueRule
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation removeLeagueRule($leagueId: Int!, $rule: LeagueRule!) {
        data: removeLeagueRule(leagueId: $leagueId, rule: $rule)
      }
    `,
    { leagueId, rule }
  );

export type LeagueRuleConfig = {
  id: number;
  leagueRuleId: number;
  rounds: string;
  multiplier: number;
  excludeTopN: number;
};

export const addLeagueRuleConfig = async (
  user: Nullable<UserWithToken>,
  leagueRuleId: number,
  rounds: string,
  multiplier: number,
  excludeTopN: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueRuleConfig>>(
    gql`
      mutation addLeagueRuleConfig(
        $leagueRuleId: Int!
        $rounds: String!
        $multiplier: Float!
        $excludeTopN: Int!
      ) {
        data: addLeagueRuleConfig(
          leagueRuleId: $leagueRuleId
          rounds: $rounds
          multiplier: $multiplier
          excludeTopN: $excludeTopN
        ) {
          id
          leagueRuleId
          rounds
          multiplier
          excludeTopN
        }
      }
    `,
    { leagueRuleId, rounds, multiplier, excludeTopN }
  );

export const updateLeagueRuleConfig = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: { rounds?: string; multiplier?: number; excludeTopN?: number }
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueRuleConfig>>(
    gql`
      mutation updateLeagueRuleConfig(
        $id: Int!
        $rounds: String
        $multiplier: Float
        $excludeTopN: Int
      ) {
        data: updateLeagueRuleConfig(
          id: $id
          rounds: $rounds
          multiplier: $multiplier
          excludeTopN: $excludeTopN
        ) {
          id
          leagueRuleId
          rounds
          multiplier
          excludeTopN
        }
      }
    `,
    { id, ...input }
  );

export const removeLeagueRuleConfig = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation removeLeagueRuleConfig($id: Int!) {
        data: removeLeagueRuleConfig(id: $id)
      }
    `,
    { id }
  );

export const setLeaguePositionPoints = async (
  user: Nullable<UserWithToken>,
  leagueRuleId: number,
  rows: LeaguePositionPoints[]
) =>
  getGraphQLQueryClient(user).request<WithData<LeaguePositionPoints[]>>(
    gql`
      mutation setLeaguePositionPoints(
        $leagueRuleId: Int!
        $rows: [LeaguePositionPointsInput!]!
      ) {
        data: setLeaguePositionPoints(
          leagueRuleId: $leagueRuleId
          rows: $rows
        ) {
          minPlayers
          position
          points
        }
      }
    `,
    { leagueRuleId, rows }
  );

export const generateLeaguePlayoffTournament = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  withTop: number,
  input?: { title?: string; dateFrom?: string; dateTo?: string; formatId?: number }
) =>
  getGraphQLQueryClient(user).request<WithData<{ id: number; title: string }>>(
    gql`
      mutation generateLeaguePlayoffTournament(
        $leagueId: Int!
        $withTop: Int!
        $title: String
        $dateFrom: DateTime
        $dateTo: DateTime
        $formatId: Int
      ) {
        data: generateLeaguePlayoffTournament(
          leagueId: $leagueId
          withTop: $withTop
          title: $title
          dateFrom: $dateFrom
          dateTo: $dateTo
          formatId: $formatId
        ) {
          id
          title
        }
      }
    `,
    {
      leagueId,
      withTop,
      title: input?.title,
      dateFrom: input?.dateFrom,
      dateTo: input?.dateTo,
      formatId: input?.formatId,
    }
  );

export const addLeaguePlayer = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; userId: number; banned: boolean }>
  >(
    gql`
      mutation addLeaguePlayer($leagueId: Int!, $userId: Int!) {
        data: addLeaguePlayer(leagueId: $leagueId, userId: $userId) {
          id
          userId
          banned
        }
      }
    `,
    { leagueId, userId }
  );

export const banLeaguePlayer = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; userId: number; banned: boolean }>
  >(
    gql`
      mutation banLeaguePlayer($leagueId: Int!, $userId: Int!) {
        data: banLeaguePlayer(leagueId: $leagueId, userId: $userId) {
          id
          userId
          banned
        }
      }
    `,
    { leagueId, userId }
  );

export const unbanLeaguePlayer = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; userId: number; banned: boolean }>
  >(
    gql`
      mutation unbanLeaguePlayer($leagueId: Int!, $userId: Int!) {
        data: unbanLeaguePlayer(leagueId: $leagueId, userId: $userId) {
          id
          userId
          banned
        }
      }
    `,
    { leagueId, userId }
  );

export const syncLeaguePlayers = async (
  user: Nullable<UserWithToken>,
  leagueId: number
) =>
  getGraphQLQueryClient(user).request<WithData<number>>(
    gql`
      mutation syncLeaguePlayers($leagueId: Int!) {
        data: syncLeaguePlayers(leagueId: $leagueId)
      }
    `,
    { leagueId }
  );

export const getLeaguePayments = async (
  user: Nullable<UserWithToken>,
  leagueId: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeaguePayment[]>>(
    gql`
      query leaguePayments($leagueId: Int!) {
        data: leaguePayments(leagueId: $leagueId) {
          id
          leaguePlayerId
          tournamentId
          paidAt
          leaguePlayer {
            id
            userId
            user {
              id
              name
              lastName
            }
          }
          tournament {
            id
            title
          }
        }
      }
    `,
    { leagueId }
  );

export const markLeaguePayment = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  userId: number,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeaguePayment>>(
    gql`
      mutation markLeaguePayment(
        $leagueId: Int!
        $userId: Int!
        $tournamentId: Int!
      ) {
        data: markLeaguePayment(
          leagueId: $leagueId
          userId: $userId
          tournamentId: $tournamentId
        ) {
          id
          leaguePlayerId
          tournamentId
          paidAt
        }
      }
    `,
    { leagueId, userId, tournamentId }
  );

export const unmarkLeaguePayment = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  userId: number,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation unmarkLeaguePayment(
        $leagueId: Int!
        $userId: Int!
        $tournamentId: Int!
      ) {
        data: unmarkLeaguePayment(
          leagueId: $leagueId
          userId: $userId
          tournamentId: $tournamentId
        )
      }
    `,
    { leagueId, userId, tournamentId }
  );

export const updateTournamentLeague = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  leagueId: number,
  pointsMultiplier: number,
  roundNumber?: number | null
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentLeague>>(
    gql`
      mutation updateTournamentLeague(
        $tournamentId: Int!
        $leagueId: Int!
        $pointsMultiplier: Float!
        $roundNumber: Int
      ) {
        data: updateTournamentLeague(
          tournamentId: $tournamentId
          leagueId: $leagueId
          pointsMultiplier: $pointsMultiplier
          roundNumber: $roundNumber
        ) {
          id
          tournamentId
          leagueId
          roundNumber
          pointsMultiplier
        }
      }
    `,
    { tournamentId, leagueId, pointsMultiplier, roundNumber }
  );

export const duplicateLeague = (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<League>>(
    gql`
      mutation duplicateLeague($id: Int!) {
        data: duplicateLeague(id: $id) {
          id
        }
      }
    `,
    { id }
  );

export const deleteLeague = (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteLeague($id: Int!) {
        data: deleteLeague(id: $id)
      }
    `,
    { id }
  );

export const uploadLeagueOgImage = (
  leagueId: number,
  file: File,
  config?: { headers?: Record<string, string> }
) => {
  const formData = new FormData();
  formData.append('image', file);

  return axiosClient.post(`/leagues/${leagueId}/og-image`, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteLeagueOgImage = (leagueId: number) => {
  return axiosClient.delete(`/leagues/${leagueId}/og-image`);
};

// Duelist Kingdom - Star Types & Functions

export type LeagueStarTransaction = {
  id: number;
  leagueId: number;
  tournamentId: number;
  fromUserId: number;
  toUserId: number;
  starsBet: number;
  pointsBet: number;
  forced: boolean;
  createdAt: string;
  fromUser: { id: number; name: string; lastName: string } | null;
  toUser: { id: number; name: string; lastName: string } | null;
};

export type LeaguePlayerStars = {
  userId: number;
  stars: number;
  user: { id: number; name: string; lastName: string } | null;
};

export const getLeagueStars = async (
  user: Nullable<UserWithToken>,
  leagueId: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeaguePlayerStars[]>>(
    gql`
      query leagueStars($leagueId: Int!) {
        data: leagueStars(leagueId: $leagueId) {
          userId
          stars
          user {
            id
            name
            lastName
          }
        }
      }
    `,
    { leagueId }
  );

export const getLeagueStarTransactions = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  tournamentId?: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueStarTransaction[]>>(
    gql`
      query leagueStarTransactions($leagueId: Int!, $tournamentId: Int) {
        data: leagueStarTransactions(
          leagueId: $leagueId
          tournamentId: $tournamentId
        ) {
          id
          leagueId
          tournamentId
          fromUserId
          toUserId
          starsBet
          pointsBet
          forced
          createdAt
          fromUser {
            id
            name
            lastName
          }
          toUser {
            id
            name
            lastName
          }
        }
      }
    `,
    { leagueId, tournamentId }
  );

export const recordStarBet = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  tournamentId: number,
  fromUserId: number,
  toUserId: number,
  starsBet: number,
  pointsBet?: number,
  forced?: boolean
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueStarTransaction>>(
    gql`
      mutation recordStarBet(
        $leagueId: Int!
        $tournamentId: Int!
        $fromUserId: Int!
        $toUserId: Int!
        $starsBet: Int!
        $pointsBet: Int
        $forced: Boolean
      ) {
        data: recordStarBet(
          leagueId: $leagueId
          tournamentId: $tournamentId
          fromUserId: $fromUserId
          toUserId: $toUserId
          starsBet: $starsBet
          pointsBet: $pointsBet
          forced: $forced
        ) {
          id
          leagueId
          tournamentId
          fromUserId
          toUserId
          starsBet
          pointsBet
          forced
          createdAt
        }
      }
    `,
    { leagueId, tournamentId, fromUserId, toUserId, starsBet, pointsBet, forced }
  );

export const deleteStarBet = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteStarBet($id: Int!) {
        data: deleteStarBet(id: $id)
      }
    `,
    { id }
  );

// Extra Points - ajustes manuales de puntos por fecha

export type LeagueExtraPoints = {
  id: number;
  leagueId: number;
  tournamentId: number;
  userId: number;
  points: number;
  reason: string;
  createdAt: string;
  user: { id: number; name: string; lastName: string } | null;
};

export const getLeagueExtraPoints = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  tournamentId?: number
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueExtraPoints[]>>(
    gql`
      query leagueExtraPoints($leagueId: Int!, $tournamentId: Int) {
        data: leagueExtraPoints(
          leagueId: $leagueId
          tournamentId: $tournamentId
        ) {
          id
          leagueId
          tournamentId
          userId
          points
          reason
          createdAt
          user {
            id
            name
            lastName
          }
        }
      }
    `,
    { leagueId, tournamentId }
  );

export const setLeagueExtraPoints = async (
  user: Nullable<UserWithToken>,
  leagueId: number,
  tournamentId: number,
  userId: number,
  points: number,
  reason: string
) =>
  getGraphQLQueryClient(user).request<WithData<LeagueExtraPoints>>(
    gql`
      mutation setLeagueExtraPoints(
        $leagueId: Int!
        $tournamentId: Int!
        $userId: Int!
        $points: Int!
        $reason: String!
      ) {
        data: setLeagueExtraPoints(
          leagueId: $leagueId
          tournamentId: $tournamentId
          userId: $userId
          points: $points
          reason: $reason
        ) {
          id
          leagueId
          tournamentId
          userId
          points
          reason
          createdAt
        }
      }
    `,
    { leagueId, tournamentId, userId, points, reason }
  );

export const removeLeagueExtraPoints = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation removeLeagueExtraPoints($id: Int!) {
        data: removeLeagueExtraPoints(id: $id)
      }
    `,
    { id }
  );
